/**
 * useBillingReadOnly — derives whether the UI should switch to read-only
 * mode because the user is at the monthly allowance cap.
 *
 * Returns `true` when ALL hold:
 *   - user is not admin (admins are unmetered + never gated)
 *   - subscription row exists with limiting enabled
 *   - usage pct >= 1.00 (allowance REACHED — at-or-over cap)
 *
 * Threshold differentiation from `<MonthlyAllowanceBanner>` (2026-05-14):
 *   - Banner fires at ≥90% (soft warning, dismissible — "approaching")
 *   - Read-only kicks in at 100% (hard cap reached)
 *
 * The AND-gate on `usage_limiting_enabled === true` is load-bearing —
 * capture-only users (limiting OFF) intentionally overrun without UI gating.
 *
 * Consumers: `<ChatInput>` (disable composer), `<ConversationSidebar>`
 * onCreate (disable New Deal), `<ModelLayout>` (block edits),
 * `<NarrativeBar>` (Signal-tab composer disabled).
 */

import { useAuth } from './use-auth';
import { useBillingUsage, asBillingUsageRow } from './use-billing-usage';

export function useBillingReadOnly(): boolean {
  const { isAdmin } = useAuth();
  const { data } = useBillingUsage();
  if (isAdmin) return false;
  const row = asBillingUsageRow(data);
  if (!row) return false;
  if (!row.usage_limiting_enabled) return false;
  return row.pct >= 1.0;
}
